/**
 * @file     GridCtaButton.tsx
 * @readme   ./CLAUDE.md
 * @purpose  The surface CTA ("Watch this area", "Alert me to similar") once per
 *           hub grid, in LocationListingGrid's header, instead of on every bare
 *           card. Reads the same CardCtaProvider handle the cards read, so the
 *           click runs the same handler and analytics. Inquiry CTAs are
 *           listing-scoped and stay on each card, so this renders nothing.
 */
'use client';

import Link from 'next/link';
import Button from '@mui/material/Button';
import { useAppTheme } from '@/core/theme/useAppTheme';
import { useCardCta } from '../CardCtaContext';

export function GridCtaButton() {
  const cta = useCardCta();
  const { theme } = useAppTheme();
  if (!cta || cta.kind === 'inquiry') return null;

  const sx = {
    minHeight: 48,
    px: 5,
    whiteSpace: 'nowrap',
    textTransform: 'none',
    fontSize: 14.5,
    fontWeight: 500,
    borderRadius: `${theme.layout.radius.md}px`,
    color: theme.semanticColors.interactivePrimary,
    borderColor: theme.semanticColors.borderStrong,
    '&:hover': { borderColor: theme.semanticColors.interactivePrimary },
  } as const;

  return cta.href ? (
    <Button
      component={Link}
      href={cta.href}
      variant="outlined"
      onClick={() => cta.onActivate?.()}
      sx={sx}
    >
      {cta.label}
    </Button>
  ) : (
    <Button
      variant="outlined"
      disabled={cta.onActivate == null}
      onClick={() => cta.onActivate?.()}
      sx={sx}
    >
      {cta.label}
    </Button>
  );
}
